import styled from '@emotion/styled'
import { zodResolver } from '@hookform/resolvers/zod'
import Link from 'next/link'
import React from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { isEmpty } from '@livechat/data-utils'

import { COUNTRY_CODES } from './constants'
import FeatureToggle from 'components/FeatureToggle'
import { FeatureExplanation, FeatureItem, FeaturesList } from 'components/FeaturesList'
import InfoBanner from 'components/InfoBanner'
import { Portal } from 'components/Portal'
import { ToastKind } from 'components/Toast'
import FieldError from 'components/fields/FieldError'
import Fieldset from 'components/fields/Fieldset'
import Form from 'components/fields/Form'
import FormRow from 'components/fields/FormRow'
import { StyledInput as OGStyledInput } from 'components/fields/Input'
import Legend from 'components/fields/Legend'
import Select from 'components/fields/Select'
import { ChatsIcon, ChevronIcon, PhoneIcon } from 'components/icons'
import { useFeature } from 'hooks/useFeature'
import { useIsOnboarding } from 'hooks/useIsOnboarding'
import { HeadingControlPortalContext } from 'layouts/DefaultLayout'
import { updateFeature } from 'lib/api'
import { useAppStore } from 'lib/store'
import type { Feature } from 'types/feature'

const Container = styled.div<{ isOnboarding: boolean }>`
	display: flex;
	flex-direction: column;
	gap: 16px;
	margin-right: ${({ theme, isOnboarding }) => (!theme.isMobile && isOnboarding ? '64px' : 0)};
	max-width: 380px;
`

const Explanation = styled(FeatureExplanation)`
	margin-bottom: 0;
`

const SelectWrapper = styled.div`
	height: 46px;
	width: 200px;
	position: relative;
	cursor: pointer;
	margin-bottom: ${({ theme }) => theme.spaces.space4};
`

const StyledSelect = styled(Select)`
	position: absolute;
	inset: 0;
	padding: 0;
	color: transparent;
	background: transparent;
	cursor: pointer;
	width: 128px;

	&:focus {
		outline-style: solid;
		outline-width: 1px;
		outline-color: ${({ theme }) => theme.colors.light.surfaceInteractive};
	}
`

const CountryCode = styled.label`
	position: absolute;
	inset: 0;
	padding: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	padding: ${({ theme }) => theme.spaces.space3};
	color: ${({ theme }) => theme.colors.light.secondaryTextColor};
	border-radius: ${({ theme }) => theme.borderRadiuses.md};
	background-color: ${({ theme }) => theme.colors.light.surfaceVariant};

	${({ theme }) => theme.typography.paragraph}
`

const StyledLink = styled(Link)`
	color: ${({ theme }) => theme.colors.light.primaryTextColor};
`

const StyledInput = styled(OGStyledInput)`
	padding: ${({ theme }) => `${theme.spaces.space5} ${theme.spaces.space6}`};
`

const StyledForm = styled(Form)`
	padding: 0;
`

const StyledFormRow = styled(FormRow)`
	margin: 0;
	gap: ${({ theme }) => theme.spaces.space3};
`

const FeatureDescription = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${({ theme }) => theme.spaces.space7};
	margin-bottom: 48px;
`

const PhoneNumberFieldSet = styled(Fieldset)`
	width: 100%;
	padding-top: 0;
	padding-bottom: ${({ theme }) => theme.spaces.space4};
`

const FormSchema = z.object({
	countryCode: z.string(),
	phoneNumber: z
		.string()
		.nonempty('Phone number is required')
		.min(6, 'Phone number is too short')
		.max(15, 'Phone number is too long'),
})

type FormValues = z.infer<typeof FormSchema>

function getFormValues(feature: Feature<'whatsapp'>): FormValues {
	return {
		countryCode: feature.properties.countryCode ?? '1',
		phoneNumber: feature.properties.phoneNumber ?? '',
	}
}

function withDefaultValues(FormComponent: typeof WhatsAppForm) {
	const WithDefaultValues = React.forwardRef<HTMLFormElement, Props>((props, ref) => {
		const { feature: whatsappFeature, isFeatureLoading: isWhatsappFeatureLoading } = useFeature('whatsapp')

		if (!whatsappFeature || isWhatsappFeatureLoading) {
			return null
		}

		return (
			<FormComponent
				{...props}
				formRef={ref}
				firstSetup={isEmpty(whatsappFeature.properties)}
				defaultValues={getFormValues(whatsappFeature)}
			/>
		)
	})

	WithDefaultValues.displayName = `WithDefaultValues(${FormComponent.displayName})`

	return WithDefaultValues
}

type Props = {
	id: string
	onSubmit?: VoidFunction
	onHasErrorsChanged?(hasErrors: boolean, id: string): void
	onIsDirtyChanged?(isDirty: boolean, id: string): void
	onIsSubmittingChanged?(isSubmitting: boolean, id: string): void
	onSubmitResult?(result: ToastKind): void
}

function WhatsAppForm({
	id,
	formRef,
	defaultValues,
	firstSetup,
	onHasErrorsChanged,
	onIsDirtyChanged,
	onIsSubmittingChanged,
	onSubmit,
	onSubmitResult,
}: Props & { defaultValues: FormValues; firstSetup: boolean; formRef: React.ForwardedRef<HTMLFormElement> }) {
	const isOnboarding = useIsOnboarding()
	const store = useAppStore()
	const { feature: whatsappFeature, mutateFeature: mutateWhatsappFeature } = useFeature('whatsapp')
	const { register, reset, watch, handleSubmit, formState } = useForm({
		defaultValues,
		mode: 'onTouched',
		resolver: zodResolver(FormSchema),
	})
	const { errors, isSubmitting, isDirty, isValid, dirtyFields } = formState
	const hasErrors = !isEmpty(errors)
	const countryCode = watch('countryCode')

	const selectedCountryLabel = React.useMemo(() => {
		const [, iso, code] = COUNTRY_CODES.find(([, , code]) => code === countryCode) ?? []
		return `${iso?.toUpperCase()} (+${code})`
	}, [countryCode])

	React.useEffect(() => {
		const handler = (values: Partial<FormValues>) => {
			store.dispatch({
				type: 'whatsapp/set',
				payload: {
					enabled: !!whatsappFeature?.enabled,
					properties: {
						countryCode: values.countryCode,
						phoneNumber: values.phoneNumber,
					},
				},
			})
		}
		handler(defaultValues)
		const subscription = watch(handler)
		return () => subscription.unsubscribe()
	}, [store, watch, defaultValues, whatsappFeature])

	React.useEffect(() => {
		onHasErrorsChanged?.(hasErrors, id)
	}, [id, onHasErrorsChanged, hasErrors])

	React.useEffect(() => {
		onIsSubmittingChanged?.(isSubmitting, id)
	}, [id, isSubmitting, onIsSubmittingChanged])

	React.useEffect(() => {
		onIsDirtyChanged?.(isDirty, id)
	}, [id, isDirty, onIsDirtyChanged])

	async function handleWhatsappSwitchChange(isEnabled: boolean): Promise<{ isEnabled: boolean }> {
		try {
			await updateFeature('whatsapp', { enabled: isEnabled })
			mutateWhatsappFeature(
				(current) =>
					current ? { ...current, enabled: isEnabled } : { name: 'whatsapp', properties: {}, enabled: isEnabled },
				{ revalidate: false },
			)
			onSubmitResult?.('success')
		} catch {
			onSubmitResult?.('error')
		}

		return { isEnabled }
	}

	async function _handleSubmit(data: FormValues) {
		if (!isValid || !isDirty) {
			return
		}

		try {
			const updatedFeature = await updateFeature('whatsapp', {
				...(firstSetup && { enabled: true }),
				...(!isEmpty(dirtyFields) && {
					properties: {
						...((dirtyFields.countryCode || !whatsappFeature?.properties.countryCode) && {
							countryCode: data.countryCode,
						}),
						...(dirtyFields.phoneNumber && { phoneNumber: data.phoneNumber }),
					},
				}),
			})

			mutateWhatsappFeature(updatedFeature, { revalidate: false })
			reset(getFormValues(updatedFeature))
			onSubmit?.()
			onSubmitResult?.('success')
		} catch {
			onSubmitResult?.('error')
		}
	}

	const handleReset = (event: React.FormEvent) => {
		event.preventDefault()
		reset(defaultValues)
	}

	return (
		<>
			{!firstSetup && (
				<Portal context={HeadingControlPortalContext}>
					<FeatureToggle
						isEnabled={!!whatsappFeature?.enabled}
						onChange={handleWhatsappSwitchChange}
						ariaName="whatsapp"
						trackingName="WhatsApp"
					/>
				</Portal>
			)}
			<>
				<Container isOnboarding={isOnboarding}>
					<FeatureDescription>
						<Explanation>{`Handle customers' queries through WhatsApp application. Customers will be able to write to you using their desktop and mobile apps.`}</Explanation>
						<FeaturesList>
							<FeatureItem>
								<ChatsIcon />
								Invites to chat when the widget is minimized
							</FeatureItem>
							<FeatureItem>
								<PhoneIcon />
								Opens up a WhatsApp application
							</FeatureItem>
						</FeaturesList>
					</FeatureDescription>
					<StyledForm id={id} ref={formRef} onSubmit={handleSubmit(_handleSubmit)} onReset={handleReset}>
						<Fieldset>
							<Legend>Phone number</Legend>
							<StyledFormRow>
								<SelectWrapper>
									<CountryCode aria-hidden tabIndex={-1}>
										{selectedCountryLabel}
										<ChevronIcon />
									</CountryCode>
									<StyledSelect id="country-code" aria-label="country code" {...register('countryCode')}>
										{COUNTRY_CODES.map(([country, code, dialCode]) => (
											<option key={code} value={dialCode}>
												{country} (+{dialCode})
											</option>
										))}
									</StyledSelect>
								</SelectWrapper>
								<PhoneNumberFieldSet>
									<StyledInput
										type="tel"
										aria-label="phone number"
										{...register('phoneNumber', {
											onChange(event) {
												event.target.value = event.target.value.replace(/\D/g, '')
											},
										})}
									/>
									{errors.phoneNumber && <FieldError>{errors.phoneNumber.message}</FieldError>}
								</PhoneNumberFieldSet>
							</StyledFormRow>
						</Fieldset>
					</StyledForm>
					{!isOnboarding && (
						<InfoBanner>
							{`When the widget is minimized customer will see a message with the same text and avatar as in the `}
							<StyledLink href="/configurator/widget-content/contact">Contact card →</StyledLink>
						</InfoBanner>
					)}
				</Container>
			</>
		</>
	)
}

WhatsAppForm.displayName = 'WhatsappForm'

export default withDefaultValues(WhatsAppForm)
